import { useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useClients } from "@/features/clients/api";
import {
  dispatchToForm,
  useCreateDispatchNote,
  useDispatchableFulfillments,
  useDispatchNote,
  useUpdateDispatchNote,
} from "@/features/dispatch/api";
import { canEditDispatch } from "@/features/dispatch/constants";
import {
  dispatchFormSchema,
  emptyDispatchForm,
  type DispatchFormValues,
} from "@/features/dispatch/schema";
import { useDrivers, useTransporters, useVehicles } from "@/features/fleet/api";
import { fmtQty } from "@/features/inventory/constants";
import { useInvoices } from "@/features/invoices/api";
import { useSalesOrder, useSalesOrders } from "@/features/sales-orders/api";
import { cn } from "@/lib/utils";

export function DispatchNewPage() {
  return (
    <ErrorBoundary title="Dispatch form crashed">
      <DispatchFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function DispatchEditPage({ dnId }: { dnId: string }) {
  return (
    <ErrorBoundary title="Dispatch form crashed">
      <DispatchFormPage mode="edit" dnId={dnId} />
    </ErrorBoundary>
  );
}

function DispatchFormPage({ mode, dnId }: { mode: "create" | "edit"; dnId?: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { so?: string };
  const isEdit = mode === "edit";
  const { data: note, isLoading } = useDispatchNote(isEdit ? dnId : undefined);
  const { data: salesOrders = [] } = useSalesOrders();
  const { data: clients = [] } = useClients();
  const { data: transporters = [] } = useTransporters();
  const { data: vehicles = [] } = useVehicles();
  const { data: drivers = [] } = useDrivers();
  const createDn = useCreateDispatchNote();
  const updateDn = useUpdateDispatchNote();
  const hydratedRef = useRef(false);

  const form = useForm<DispatchFormValues>({
    resolver: zodResolver(dispatchFormSchema),
    defaultValues: emptyDispatchForm,
    mode: "onBlur",
  });

  const values = useWatch({ control: form.control });
  const soId = values.sales_order_id || undefined;
  const { data: so } = useSalesOrder(soId);
  const { data: fulfillments = [], isLoading: loadingFul } = useDispatchableFulfillments(soId);
  const { data: invoices = [] } = useInvoices(
    values.client_id ? { clientId: values.client_id } : undefined,
  );
  const {
    formState: { errors },
  } = form;

  const editable = !isEdit || (note ? canEditDispatch(note.status) : true);

  const openSos = useMemo(
    () =>
      salesOrders.filter((s) =>
        ["confirmed", "in_progress", "partial", "closed"].includes(s.status),
      ),
    [salesOrders],
  );

  const issuedInvoices = useMemo(
    () =>
      invoices.filter(
        (i) =>
          i.status === "issued" &&
          (!soId || i.sales_order_id === soId || !i.sales_order_id),
      ),
    [invoices, soId],
  );

  const transporterOptions = useMemo(
    () =>
      transporters
        .filter((t) => t.is_active)
        .map((t) => ({
          id: t.id,
          label: `${t.code} — ${t.name}`,
          keywords: `${t.code} ${t.name} ${t.phone ?? ""}`,
          meta: t.phone ?? undefined,
        })),
    [transporters],
  );

  const vehicleOptions = useMemo(() => {
    const tpId = values.transporter_id;
    return vehicles
      .filter((v) => v.is_active && (!tpId || v.transporter_id === tpId || !v.transporter_id))
      .map((v) => ({
        id: v.id,
        label: v.vehicle_number,
        keywords: `${v.vehicle_number} ${v.vehicle_type} ${v.transporter_name ?? ""}`,
        meta: v.transporter_name ?? v.vehicle_type,
      }));
  }, [vehicles, values.transporter_id]);

  const driverOptions = useMemo(() => {
    const tpId = values.transporter_id;
    return drivers
      .filter((d) => d.is_active && (!tpId || d.transporter_id === tpId || !d.transporter_id))
      .map((d) => ({
        id: d.id,
        label: d.name,
        keywords: `${d.name} ${d.phone ?? ""} ${d.license_number ?? ""}`,
        meta: d.phone ?? d.transporter_name ?? undefined,
      }));
  }, [drivers, values.transporter_id]);

  useEffect(() => {
    if (!isEdit || !note || hydratedRef.current) return;
    form.reset(dispatchToForm(note));
    hydratedRef.current = true;
  }, [isEdit, note, form]);

  useEffect(() => {
    if (isEdit || !search.so || hydratedRef.current) return;
    form.setValue("sales_order_id", search.so);
  }, [isEdit, search.so, form]);

  useEffect(() => {
    if (!so || isEdit) return;
    form.setValue("client_id", so.client_id);
    form.setValue("ship_to_text", so.ship_to_text ?? so.bill_to_text ?? "");
    const client = clients.find((c) => c.id === so.client_id);
    if (client) {
      form.setValue("transporter_name", client.preferred_transporter ?? "");
      form.setValue("instructions", client.dispatch_instructions ?? "");
    }
  }, [so, clients, isEdit, form]);

  function toggleFulfillment(f: (typeof fulfillments)[0], checked: boolean) {
    const current = form.getValues("lines") ?? [];
    if (checked) {
      form.setValue("lines", [
        ...current,
        {
          sales_order_line_id: f.sales_order_line_id,
          sales_order_fulfillment_id: f.fulfillment_id,
          inventory_batch_id: f.inventory_batch_id,
          quantity: f.remaining_qty,
          weight: f.remaining_weight,
          notes: "",
          batch_no: f.batch_no ?? "",
          material_name: f.material_name ?? "",
          line_no: f.line_no,
        },
      ]);
    } else {
      form.setValue(
        "lines",
        current.filter((l) => l.sales_order_fulfillment_id !== f.fulfillment_id),
      );
    }
  }

  const selectedIds = new Set(
    (values.lines ?? []).map((l) => l.sales_order_fulfillment_id).filter(Boolean),
  );

  async function onSave() {
    const ok = await form.trigger();
    if (!ok) return;
    const payload = form.getValues();
    if (isEdit && dnId) {
      const updated = await updateDn.mutateAsync({ id: dnId, values: payload });
      navigate({ to: "/dispatch/$dnId", params: { dnId: updated.id } });
    } else {
      const created = await createDn.mutateAsync(payload);
      navigate({ to: "/dispatch/$dnId", params: { dnId: created.id } });
    }
  }

  useDocumentSaveShortcut(() => {
    if (editable) void onSave();
  });

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (isEdit && note && !canEditDispatch(note.status)) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground">This dispatch cannot be edited.</p>
        <Button asChild size="sm" variant="outline">
          <Link to="/dispatch/$dnId" params={{ dnId: note.id }}>
            Back
          </Link>
        </Button>
      </div>
    );
  }

  const saving = createDn.isPending || updateDn.isPending;
  const totalWt = (values.lines ?? []).reduce((s, l) => s + (Number(l.weight) || 0), 0);

  return (
    <DocumentPage>
      <DocumentHeader
        title={isEdit ? `Edit ${note?.note_number ?? "dispatch"}` : "New dispatch"}
        subtitle="Select fulfilled stock · loading slip"
        back={
          <Button type="button" variant="ghost" size="icon-sm" asChild>
            {isEdit && dnId ? (
              <Link to="/dispatch/$dnId" params={{ dnId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/dispatch">
                <ArrowLeft />
              </Link>
            )}
          </Button>
        }
      />

      <FormSection title="Header" variant="plain">
        <FieldGrid>
          <Field label="Sales order" required span={5} error={errors.sales_order_id?.message}>
            <NativeSelect
              disabled={!editable || isEdit}
              {...form.register("sales_order_id", {
                onChange: () => {
                  form.setValue("lines", []);
                  form.setValue("invoice_id", "");
                },
              })}
            >
              <option value="">Select SO…</option>
              {openSos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.so_number} — {s.client_name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Date" required span={3} error={errors.dispatch_date?.message}>
            <Input type="date" disabled={!editable} {...form.register("dispatch_date")} />
          </Field>
          <Field label="Invoice" span={4} hint="Optional">
            <NativeSelect disabled={!editable || !soId} {...form.register("invoice_id")}>
              <option value="">None</option>
              {issuedInvoices.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.invoice_number}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Ship to" span={12}>
            <Textarea rows={2} disabled={!editable} {...form.register("ship_to_text")} />
          </Field>
        </FieldGrid>
      </FormSection>

      <FormSection title="Logistics" variant="plain">
        <FieldGrid>
          <Field label="Transporter" span={4} hint="Master or free text">
            <EntityPicker
              value={values.transporter_id ?? ""}
              disabled={!editable}
              placeholder="Pick transporter…"
              recentKey="transporters"
              allowClear
              options={transporterOptions}
              onChange={(id) => {
                form.setValue("transporter_id", id, { shouldDirty: true });
                const t = transporters.find((x) => x.id === id);
                if (t) form.setValue("transporter_name", t.name, { shouldDirty: true });
                if (!id) return;
                const veh = values.vehicle_id
                  ? vehicles.find((v) => v.id === values.vehicle_id)
                  : undefined;
                if (veh?.transporter_id && veh.transporter_id !== id) {
                  form.setValue("vehicle_id", "", { shouldDirty: true });
                }
                const drv = values.driver_id
                  ? drivers.find((d) => d.id === values.driver_id)
                  : undefined;
                if (drv?.transporter_id && drv.transporter_id !== id) {
                  form.setValue("driver_id", "", { shouldDirty: true });
                }
              }}
            />
          </Field>
          <Field label="Vehicle" span={3}>
            <EntityPicker
              value={values.vehicle_id ?? ""}
              disabled={!editable}
              placeholder="Pick vehicle…"
              recentKey="vehicles"
              allowClear
              options={vehicleOptions}
              onChange={(id) => {
                form.setValue("vehicle_id", id, { shouldDirty: true });
                const v = vehicles.find((x) => x.id === id);
                if (v) {
                  form.setValue("vehicle_number", v.vehicle_number, { shouldDirty: true });
                  if (v.transporter_id && !values.transporter_id) {
                    form.setValue("transporter_id", v.transporter_id, { shouldDirty: true });
                    if (v.transporter_name) {
                      form.setValue("transporter_name", v.transporter_name, { shouldDirty: true });
                    }
                  }
                  if (v.driver_id) {
                    form.setValue("driver_id", v.driver_id, { shouldDirty: true });
                    if (v.driver_name) {
                      form.setValue("driver_name", v.driver_name, { shouldDirty: true });
                    }
                  }
                }
              }}
            />
          </Field>
          <Field label="Driver" span={3}>
            <EntityPicker
              value={values.driver_id ?? ""}
              disabled={!editable}
              placeholder="Pick driver…"
              recentKey="drivers"
              allowClear
              options={driverOptions}
              onChange={(id) => {
                form.setValue("driver_id", id, { shouldDirty: true });
                const d = drivers.find((x) => x.id === id);
                if (d) form.setValue("driver_name", d.name, { shouldDirty: true });
              }}
            />
          </Field>
          <Field label="LR / e-way" span={2}>
            <Input disabled={!editable} {...form.register("lr_number")} />
          </Field>
          <Field label="Transporter text" span={4} hint="Override / ad-hoc">
            <Input disabled={!editable} {...form.register("transporter_name")} />
          </Field>
          <Field label="Vehicle no." span={3}>
            <Input disabled={!editable} {...form.register("vehicle_number")} placeholder="GJ-01-XX" />
          </Field>
          <Field label="Driver name" span={3}>
            <Input disabled={!editable} {...form.register("driver_name")} />
          </Field>
          <Field label="Instructions" span={12}>
            <Input disabled={!editable} {...form.register("instructions")} />
          </Field>
        </FieldGrid>
      </FormSection>

      <FormSection title="Lines from fulfillments" variant="plain">
        {!soId ? (
          <p className="text-xs text-muted-foreground">Select a sales order first.</p>
        ) : loadingFul ? (
          <p className="text-xs text-muted-foreground">Loading fulfillments…</p>
        ) : fulfillments.length === 0 && !(values.lines ?? []).length ? (
          <p className="text-xs text-muted-foreground">
            No undispached fulfillments on this SO. Fulfill stock first.
          </p>
        ) : (
          <div className="overflow-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-wide text-muted-foreground">
                  <th className="w-8 px-2 py-1.5" />
                  <th className="px-2 py-1.5">Line</th>
                  <th className="px-2 py-1.5">Batch</th>
                  <th className="px-2 py-1.5">Material</th>
                  <th className="px-2 py-1.5 text-right">Remain wt</th>
                  <th className="px-2 py-1.5 text-right">Dispatch wt</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {fulfillments.map((f) => {
                  const checked = selectedIds.has(f.fulfillment_id);
                  const lineIdx = (values.lines ?? []).findIndex(
                    (l) => l.sales_order_fulfillment_id === f.fulfillment_id,
                  );
                  return (
                    <tr
                      key={f.fulfillment_id}
                      className={cn(
                        "border-b border-border/70",
                        checked ? "bg-primary/5" : "hover:bg-muted/30",
                      )}
                    >
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          disabled={!editable}
                          checked={checked}
                          onChange={(e) => toggleFulfillment(f, e.target.checked)}
                        />
                      </td>
                      <td className="px-2 py-1 tabular-nums">#{f.line_no}</td>
                      <td className="px-2 py-1 font-mono text-xs">{f.batch_no}</td>
                      <td className="px-2 py-1 text-xs">{f.material_name}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                        {fmtQty(f.remaining_weight)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {checked && lineIdx >= 0 ? (
                          <Input
                            type="number"
                            step="0.001"
                            disabled={!editable}
                            className="h-7 w-24 text-right"
                            value={values.lines?.[lineIdx]?.weight ?? 0}
                            onChange={(e) =>
                              form.setValue(
                                `lines.${lineIdx}.weight`,
                                Number(e.target.value) || 0,
                                { shouldValidate: true },
                              )
                            }
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {checked && lineIdx >= 0 ? (
                          <Input
                            type="number"
                            step="0.001"
                            disabled={!editable}
                            className="h-7 w-20 text-right"
                            value={values.lines?.[lineIdx]?.quantity ?? 0}
                            onChange={(e) =>
                              form.setValue(
                                `lines.${lineIdx}.quantity`,
                                Number(e.target.value) || 0,
                                { shouldValidate: true },
                              )
                            }
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {typeof errors.lines?.message === "string" ? (
          <p className="mt-1 text-2xs text-destructive">{errors.lines.message}</p>
        ) : null}
      </FormSection>

      <CollapsibleBlock title="Notes" defaultOpen={Boolean(values.notes)}>
        <Textarea rows={2} disabled={!editable} {...form.register("notes")} />
      </CollapsibleBlock>

      <DocumentStickyBar>
        <div className="text-2xs text-muted-foreground">
          {(values.lines ?? []).length} line(s) · wt {fmtQty(totalWt)} · Ctrl+S
        </div>
        <div className="flex gap-1.5">
          <Button type="button" variant="outline" size="sm" asChild>
            {isEdit && dnId ? (
              <Link to="/dispatch/$dnId" params={{ dnId }}>
                Cancel
              </Link>
            ) : (
              <Link to="/dispatch">Cancel</Link>
            )}
          </Button>
          <Button type="button" size="sm" disabled={!editable || saving} onClick={() => void onSave()}>
            {saving ? <Loader2 className="animate-spin" /> : <Plus />}
            Save draft
          </Button>
        </div>
      </DocumentStickyBar>
    </DocumentPage>
  );
}
